import React from "react";

import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  RotateCw,
  Rows3,
  Search,
} from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import type {
  XFactorDecisionAttribute,
  XFactorDisc,
  XFactorExecProfile,
} from "../../../Types/XFactorViewsRendering";
import { SourceList } from "../../XFactorBriefings/components/Primitives";
import {
  buildExecProfiles,
  buildLeadershipExtras,
  citationsOf,
  getSection,
} from "../lib/derive";

const initials = (name: string | null) =>
  (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0])
    .join("")
    .toUpperCase();

const DISC_AXES: Array<[keyof XFactorDisc, string, string]> = [
  ["d", "D", "Dominance"],
  ["i", "I", "Influence"],
  ["s", "S", "Steadiness"],
  ["c", "C", "Conscientiousness"],
];

const AXIS_COLOR: Record<string, string> = {
  d: "#ef4444",
  i: "#e9b409",
  s: "#22c55e",
  c: "#3b82f6",
};

/**
 * The back of an executive card: the DISC axes and the decision-making profile.
 *
 * Each axis prints its raw score and its share of the four, which is how
 * xfactor shows the "82 30%" pairing.
 */
/**
 * The label column both DISC sections share.
 *
 * Declared once so Decision making and Influence style line up with each other
 * rather than each sizing to its own longest label — the two read as one table
 * down the card, which is how xfactor presents them.
 */
const ATTR_GRID = "grid grid-cols-[104px_minmax(0,1fr)] gap-x-2 gap-y-0.5";

/**
 * Minimum height for every profile card.
 *
 * The cards come out around 275px from their own content, which reads as squat
 * against their width — in the two-column Executive Profiles grid they are over
 * 700px wide. This floor gives them a less letterboxed proportion and the DISC
 * face more room before it scrolls.
 *
 * One value rather than one per surface: `ProfileCard` is the single component
 * behind the leadership roster, the profiles modal, the Executive Profiles
 * block and the stakeholder map, and those grids run at two and three columns.
 * 24rem suits the wide case without making the three-column cards absurd.
 */
const PROFILE_CARD_MIN_H = "min-h-[24rem]";

/**
 * One titled block of `{attribute, value, evidence}` rows.
 *
 * `minmax(0,1fr)` rather than `1fr` is load-bearing: a bare `1fr` track carries
 * `min-width:auto`, so it refuses to shrink below its content and long evidence
 * prose pushes the card wider instead of wrapping — which is what made the face
 * scroll sideways. Confidence (`high` / `medium`) is deliberately not rendered:
 * it qualifies the model's own guess and reads as a score of the person.
 */
const AttributeSection: React.FC<{
  title: string;
  summary?: string | null;
  attributes: XFactorDecisionAttribute[];
}> = ({ title, summary, attributes }) => {
  if (!summary && !attributes.length) return null;
  return (
    <section className="mt-3">
      <h4
        className="mb-1 text-[10px] font-semibold uppercase tracking-wide"
        style={{ color: ACCENT }}
      >
        {title}
      </h4>
      {summary ? (
        <p className="mb-1.5 break-words text-[11px] leading-snug text-gray-700">
          {summary}
        </p>
      ) : null}
      <dl className="space-y-1.5">
        {attributes.map(a => (
          <div key={a.attribute} className={ATTR_GRID}>
            {/* Both cells carry the same font-size and leading. Without them on
                the `dd` it inherits the card's larger size, so its first line
                box is taller than the label's and the value sits a few pixels
                low — the rows read as misaligned even though the grid is
                correct. `items-baseline` is not enough: the `dd` wraps a span
                and a paragraph, so it is the block's line box that has to
                match, not a baseline. */}
            <dt className="break-words text-[11px] leading-snug text-gray-500">
              {a.attribute}
            </dt>
            <dd className="min-w-0 text-[11px] leading-snug">
              <span className="break-words font-semibold text-gray-900">
                {a.value}
              </span>
              {a.evidence ? (
                <p className="mt-0.5 break-words text-[11px] leading-snug text-gray-500">
                  {a.evidence}
                </p>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const DiscFace: React.FC<{ profile: XFactorExecProfile }> = ({ profile }) => {
  const disc = profile.disc;
  return (
    /* Vertical scroll only. The face is taller than the profile side and its
       length varies per person, so it scrolls within the card rather than
       stretching the grid row; overflow-x-hidden makes a stray wide child wrap
       instead of reintroducing the sideways scroll. */
    /* No max-height here any more: the card's own box constrains this face and
       provides the scroll, so the DISC side never dictates the row height. */
    <div className="px-3 pb-3">
      {/* The scale note sits opposite the heading, so it lands under the card's
          View Profile control rather than adding a row of its own. */}
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span
          className="text-[10px] font-semibold uppercase tracking-wide"
          style={{ color: ACCENT }}
        >
          DISC Profile
        </span>
        <span className="text-[10px] text-gray-900">Scores are out of 100</span>
      </div>

      {/* `minmax(0,1fr)` again: with plain grid-cols-4 the track will not go
          narrower than its longest word, so "Conscientiousness" pushed its tile
          past the card edge instead of wrapping. */}
      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))] gap-2">
        {DISC_AXES.map(([key, letter, label]) => {
          const value = (disc?.[key] as number | null) ?? null;
          return (
            <div
              key={letter}
              className="min-w-0 rounded-md border border-gray-200 px-1.5 py-1.5 text-center"
            >
              <div
                className="text-sm font-bold"
                style={{ color: AXIS_COLOR[key as string] }}
              >
                {letter}
              </div>
              <div
                className="hyphens-auto break-words text-[9px] leading-tight text-gray-500"
                lang="en"
                title={label}
              >
                {label}
              </div>
              {/* Raw score only. The share-of-four percentage was a second
                  number in the same box saying something different, and the
                  scale note above now covers what the number means. */}
              <div className="mt-0.5 text-xs font-semibold text-gray-900">
                {value == null ? "—" : value}
              </div>
              <div
                className="mt-1 h-0.5 rounded-full"
                style={{
                  backgroundColor: AXIS_COLOR[key as string],
                  opacity: value == null ? 0.15 : 1,
                }}
              />
            </div>
          );
        })}
      </div>

      {disc?.communicationStyle ? (
        <p className="mt-2 text-[11px] leading-snug text-gray-700">
          <span className="font-semibold">Communication: </span>
          {disc.communicationStyle}
        </p>
      ) : null}
      {disc?.decisionMakingStyle ? (
        <p className="text-[11px] leading-snug text-gray-700">
          <span className="font-semibold">Decision style: </span>
          {disc.decisionMakingStyle}
        </p>
      ) : null}

      <AttributeSection
        title="Decision making"
        attributes={profile.decisionAttributes}
      />
      <AttributeSection
        title="Influence style"
        summary={profile.influenceStyle?.summary}
        attributes={profile.influenceStyle?.attributes ?? []}
      />
    </div>
  );
};

const FLIP_MS = 190;

/**
 * The half-flip a profile card turns on.
 *
 * A true 180° flip needs both faces stacked at one fixed height, which these
 * cards do not have — the DISC face is a different length from the profile. So
 * the card rotates out to edge-on (90°), swaps its face while it is invisible,
 * and rotates back in from the other side. The eye reads it as one flip, and
 * the two faces are free to be different heights.
 *
 * Honours `prefers-reduced-motion` by swapping instantly.
 */
const useHalfFlip = (swap: () => void) => {
  const [phase, setPhase] = React.useState<"idle" | "out" | "in">("idle");
  const timers = React.useRef<number[]>([]);

  React.useEffect(
    () => () => {
      timers.current.forEach(window.clearTimeout);
    },
    []
  );

  const toggle = () => {
    if (phase !== "idle") return;
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    )?.matches;
    if (reduced) {
      swap();
      return;
    }
    setPhase("out");
    timers.current.push(
      window.setTimeout(() => {
        swap();
        setPhase("in");
        // One frame at the far edge, so the rotate-back actually animates
        // instead of being collapsed into the same style recalculation.
        timers.current.push(window.setTimeout(() => setPhase("idle"), 20));
      }, FLIP_MS)
    );
  };

  // "in" carries no transition on purpose: it is the frame that jumps the card
  // to the far edge. The animation back is the "idle" style that follows it.
  const easing =
    phase === "in"
      ? "none"
      : `transform ${FLIP_MS}ms ${phase === "out" ? "ease-in" : "ease-out"}, ` +
        `opacity ${FLIP_MS}ms ${phase === "out" ? "ease-in" : "ease-out"}`;

  const faceStyle: React.CSSProperties = {
    transformOrigin: "center",
    transition: easing,
    transform:
      phase === "out"
        ? "rotateY(90deg)"
        : phase === "in"
          ? "rotateY(-90deg)"
          : "rotateY(0deg)",
    opacity: phase === "idle" ? 1 : 0,
  };

  return { toggle, faceStyle };
};

export const ProfileCard: React.FC<{
  profile: XFactorExecProfile;
  /**
   * Extra lines under the profile face. The Executive Brief hangs each
   * stakeholder's engagement angle here — it belongs to the brief, not to the
   * person, so it does not follow the card onto the DISC face.
   */
  footer?: React.ReactNode;
}> = ({ profile, footer }) => {
  const [flipped, setFlipped] = React.useState(false);
  const canFlip = Boolean(profile.disc || profile.decisionAttributes.length);
  const flip = useHalfFlip(() => setFlipped(f => !f));

  return (
    /* `h-full` makes the card fill its grid cell rather than stopping at its
       own content, so every card in a row ends on the same line whichever face
       it is showing. Without it the grid cell stretches and the card's border
       does not, which reads as ragged rows.

       The min-height is a floor, not a fixed height: a card with a long bio
       still grows past it, and its row grows with it. Because this is the one
       component behind the leadership roster, the profiles modal, the
       Executive Profiles block and the stakeholder map, the floor is the same
       on all four without each surface setting its own. */
    <div
      className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white ${PROFILE_CARD_MIN_H}`}
    >
      {/* Header bar: name · title, with the flip control on the right. */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <p className="min-w-0 truncate text-[11px]">
          <span className="font-semibold" style={{ color: ACCENT }}>
            {profile.name}
          </span>
          {profile.title ? (
            <span className="text-gray-500"> · {profile.title}</span>
          ) : null}
        </p>
        {canFlip ? (
          <button
            type="button"
            onClick={flip.toggle}
            aria-label={flipped ? "View profile" : "View DISC profile"}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium transition-colors hover:bg-purple-50"
            style={{ color: ACCENT }}
          >
            <RotateCw
              size={11}
              className="transition-transform duration-500"
              style={{ transform: `rotate(${flipped ? 180 : 0}deg)` }}
            />
            {flipped ? "View Profile" : "View DISC Profile"}
          </button>
        ) : null}
      </div>

      {/* The profile face sets the card's height and keeps it, even while the
          DISC face is showing — it stays mounted and merely invisible. The DISC
          face is then overlaid and scrolls within that box, so flipping a card
          never grows its row and leaves its neighbours padded with whitespace.
          `flex-1` lets the box take the row height the grid hands down. */}
      <div style={{ perspective: 900 }} className="relative flex-1">
        <div style={flip.faceStyle} className="h-full">
          {flipped ? (
            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
              <DiscFace profile={profile} />
            </div>
          ) : null}
          <div
            className={`p-4 pt-3 ${flipped ? "invisible" : ""}`}
            aria-hidden={flipped}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
                {initials(profile.name)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {profile.name}
                </p>
                {profile.title ? (
                  <p className="text-xs text-gray-500">{profile.title}</p>
                ) : null}
              </div>
            </div>

            {profile.bio ? (
              <p className="mt-3 text-xs leading-snug text-gray-600">
                {profile.bio}
              </p>
            ) : null}

            {profile.decisionMaking ? (
              <p className="mt-2 text-xs leading-snug text-gray-500">
                <span className="font-semibold">Decision making: </span>
                {profile.decisionMaking}
              </p>
            ) : null}

            {profile.previous || profile.education ? (
              <div className="mt-3 rounded bg-gray-50 px-2.5 py-2 text-[11px] leading-snug text-gray-600">
                {profile.previous ? (
                  <p>
                    <span className="font-semibold">Previous: </span>
                    {profile.previous}
                  </p>
                ) : null}
                {profile.education ? (
                  <p>
                    <span className="font-semibold">Education: </span>
                    {profile.education}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* No attribute chips (Function, Category, Org scope, Rev/Budget,
                  Board). `profile.attributes` is still derived because the
                  modal's search box matches against those values — dropping the
                  derivation would silently narrow search to name, title and
                  bio. */}

            {footer ? <div className="mt-2">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Executive profiles, derived from the leadership section.
 *
 * Two layouts, as in xfactor: a grid for scanning and a single-card rolodex for
 * reading one profile at a time with the sources underneath.
 */
const ExecProfilesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accountName: string;
  digestDate?: string | null;
  data?: XFactorDigestData | null;
}> = ({ open, onClose, accountName, digestDate, data }) => {
  const profiles = React.useMemo(() => buildExecProfiles(data), [data]);
  const extras = React.useMemo(() => buildLeadershipExtras(data), [data]);
  const citations = React.useMemo(
    () => citationsOf(getSection(data, "leadership")),
    [data]
  );
  const [query, setQuery] = React.useState("");
  const [rolodex, setRolodex] = React.useState(false);
  const [cursor, setCursor] = React.useState(0);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(p =>
      [p.name, p.title, p.bio, ...p.attributes.map(a => a.value)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [profiles, query]);

  React.useEffect(() => setCursor(0), [query, rolodex]);

  const current = filtered[Math.min(cursor, Math.max(filtered.length - 1, 0))];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span>
          Executive profile cards —{" "}
          <span style={{ color: ACCENT }}>{accountName}</span>
        </span>
      }
      subtitle={digestDate}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name, title, function, archetype…"
            className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#ad46ff]"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filtered.length} of {profiles.length}
        </span>
        <button
          type="button"
          onClick={() => setRolodex(r => !r)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          {rolodex ? <LayoutGrid size={14} /> : <Rows3 size={14} />}
          {rolodex ? "Grid" : "Rolodex"}
        </button>
      </div>

      {!profiles.length ? (
        <p className="text-sm italic text-gray-400">
          No executive profiles in this briefing&apos;s leadership section.
        </p>
      ) : rolodex ? (
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={cursor <= 0}
              onClick={() => setCursor(c => Math.max(0, c - 1))}
              aria-label="Previous profile"
              className="rounded p-1.5 text-gray-400 transition-colors enabled:hover:bg-gray-100 enabled:hover:text-gray-700 disabled:opacity-30"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="min-w-0 flex-1">
              {current ? <ProfileCard profile={current} /> : null}
            </div>
            <button
              type="button"
              disabled={cursor >= filtered.length - 1}
              onClick={() =>
                setCursor(c => Math.min(filtered.length - 1, c + 1))
              }
              aria-label="Next profile"
              className="rounded p-1.5 text-gray-400 transition-colors enabled:hover:bg-gray-100 enabled:hover:text-gray-700 disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {filtered.map((p, i) => (
              <button
                key={`${p.name}-${i}`}
                type="button"
                onClick={() => setCursor(i)}
                className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                  i === cursor
                    ? "bg-purple-50 font-medium text-[#ad46ff]"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>

          {citations.length ? (
            <div className="mt-5">
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Sources
              </h4>
              <SourceList citations={citations} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p, i) => (
            <ProfileCard key={`${p.name}-${i}`} profile={p} />
          ))}
        </div>
      )}

      {extras.boardSize != null || extras.boardHighlights.length ? (
        <div className="mt-6">
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Board composition
            {extras.boardSize != null ? ` · ${extras.boardSize} members` : ""}
          </h4>
          <ul className="space-y-1">
            {extras.boardHighlights.map((h, i) => (
              <li
                key={i}
                className="text-xs leading-snug text-gray-600 before:mr-1.5 before:text-gray-300 before:content-['•']"
              >
                {h}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {extras.changes.length ? (
        <div className="mt-6">
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Leadership changes
          </h4>
          <div className="grid gap-2 md:grid-cols-2">
            {extras.changes.map((c, i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{c.title}</p>
                  {c.tag ? (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
                      {c.tag}
                    </span>
                  ) : null}
                </div>
                {c.body ? (
                  <p className="mt-1 text-xs leading-snug text-gray-600">
                    {c.body}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default ExecProfilesModal;
